import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import { toast } from "react-toastify";
import {
  Table,
  Grid,
  Button,
  Container,
  Confirm,
  Dimmer,
  Loader,
  Header,
  Icon,
  Segment,
  Label,
} from "semantic-ui-react";
import FuseContentModal from "../../../components/generic/FuseContentModal";
import * as ACTIVATION_RULES from "../../../actions/activationRulesActions";
import ActivationRuleDetails from "./ActivationRuleDetails";
import ActivationRuleLogDetails from "./ActivationRuleLogDetails";

// Constants
const PERMISSIONS = {
  DISTRIBUTION_RULES: "Distribution Rules",
  OTHER: "Other"
};

const ACTIONS = {
  READ: "READ",
  CREATE: "CREATE",
  UPDATE: "UPDATE",
  DELETE: "DELETE",
  EXECUTE: "EXECUTE",
  ADMIN: "admin"
};

const TARGET_AUDIENCE_LABELS = {
  mobileUsers: { color: 'blue', text: 'Mobile App' },
  whatsAppUsers: { color: 'green', text: 'WhatsApp' },
  both: { color: 'teal', text: 'Both Users' }
};

// Utility Functions
const updateState = (setState, updates) => {
  setState(prevState => ({ ...prevState, ...updates }));
};

const hasPermissions = (userPermissions, category) => {
  return !_.isEmpty(userPermissions) && userPermissions.hasOwnProperty(category);
};

const isAdmin = (userPermissions) => {
  return hasPermissions(userPermissions, PERMISSIONS.OTHER) &&
    userPermissions[PERMISSIONS.OTHER].actions[ACTIONS.ADMIN];
};

const canReadActivationRule = (userPermissions) => {
  return (hasPermissions(userPermissions, PERMISSIONS.DISTRIBUTION_RULES) &&
    userPermissions[PERMISSIONS.DISTRIBUTION_RULES].actions[ACTIONS.READ]) ||
    isAdmin(userPermissions);
};

const canCreateActivationRule = (userPermissions) => {
  return (hasPermissions(userPermissions, PERMISSIONS.DISTRIBUTION_RULES) &&
    canReadActivationRule(userPermissions) &&
    userPermissions[PERMISSIONS.DISTRIBUTION_RULES].actions[ACTIONS.CREATE]) ||
    isAdmin(userPermissions);
};

const canUpdateActivationRule = (userPermissions) => {
  return (hasPermissions(userPermissions, PERMISSIONS.DISTRIBUTION_RULES) &&
    canReadActivationRule(userPermissions) &&
    userPermissions[PERMISSIONS.DISTRIBUTION_RULES].actions[ACTIONS.UPDATE]) ||
    isAdmin(userPermissions);
};

const canDeleteActivationRule = (userPermissions) => {
  return (hasPermissions(userPermissions, PERMISSIONS.DISTRIBUTION_RULES) &&
    canReadActivationRule(userPermissions) &&
    userPermissions[PERMISSIONS.DISTRIBUTION_RULES].actions[ACTIONS.DELETE]) ||
    isAdmin(userPermissions);
};

const canExecuteActivationRule = (userPermissions) => {
  return (hasPermissions(userPermissions, PERMISSIONS.DISTRIBUTION_RULES) &&
    canReadActivationRule(userPermissions) &&
    userPermissions[PERMISSIONS.DISTRIBUTION_RULES].actions[ACTIONS.EXECUTE]) ||
    isAdmin(userPermissions);
};

// Reusable Components
const StatusLabel = ({ status }) => (
  <Label
    size='mini'
    as='a'
    tag
    color={status === "active" ? 'green' : 'red'}
    style={{ marginBottom: 5 }}
  >
    {status === "active" ? "Active" : "Inactive"}
  </Label>
);

const TargetAudienceLabel = ({ targetAudience }) => {
  const config = TARGET_AUDIENCE_LABELS[targetAudience] || TARGET_AUDIENCE_LABELS.both;
  return (
    <Label size='mini' as='a' tag color={config.color} style={{ marginBottom: 5 }}>
      {config.text}
    </Label>
  );
};

const MaxUsersLabel = ({ maxNumUsers }) => (
  maxNumUsers ? (
    <Label size='mini' as='a' tag color='yellow' style={{ marginBottom: 5 }}>
      Max Users: {maxNumUsers}
    </Label>
  ) : null
);

export function ActivationRulesPage(props) {
  const [state, setState] = useState({
    selectedActivationRule: {},
    isEditActivationRuleOpen: false,
    deleteActRuleCandidate: {},
    showConfirmDeleteActRule: false,
    showExecuteActivationRule: false,
    activationRule: {}
  });

  useEffect(() => {
    if (!_.isEmpty(props.tenant) && props.isPermissionsFetched) {
      if (canReadActivationRule(props.userPermissions)) {
        props.fetchActivationRulesAction(props.tenant);
      } else {
        toast.error("You do not have permission to view distribution rules.");
      }
    }
  }, [props.tenant, props.isPermissionsFetched]);

  const handleDeleteActivationRule = (e, rule) => {
    e.stopPropagation();
    updateState(setState, {
      deleteActRuleCandidate: rule,
      showConfirmDeleteActRule: true
    });
  };

  const confirmDeleteRule = () => {
    props.deleteActivationRuleAction(state.deleteActRuleCandidate, props.tenant);
    updateState(setState, {
      showConfirmDeleteActRule: false,
      deleteActRuleCandidate: {}
    });
  };

  const handleExecuteRule = (activationRule) => {
    updateState(setState, {
      activationRule: activationRule,
      showExecuteActivationRule: true
    });
  };

  const confirmExecuteRule = () => {
    props.executeActivartionRuleAction(state.activationRule);
    updateState(setState, { showExecuteActivationRule: false });
  };

  const renderEmptyState = () => (
    <Segment>
      <Header icon>
        <Icon name="file" color="black" />
        There Aren't Any Distribution Rules
      </Header>
    </Segment>
  );

  const renderLoadingState = () => (
    <div style={{ height: "200px", margin: "10px 0px" }}>
      <Dimmer inverted active>
        <Loader content="Loading" />
      </Dimmer>
    </div>
  );

  const renderRulesTable = () => {
    const sortedRules = [...props.activationRules].sort((a, b) => a.name.localeCompare(b.name));

    return (
      <Segment>
        <Table striped compact size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="nectaPrimaryBg" style={{ width: "10%" }}>
                Rule Name
              </Table.HeaderCell>
              <Table.HeaderCell className="nectaPrimaryBg" style={{ width: "20%" }}>
                Rule Description
              </Table.HeaderCell>
              <Table.HeaderCell className="nectaPrimaryBg" style={{ width: "25%" }}>
                Rule Details
              </Table.HeaderCell>
              <Table.HeaderCell className="nectaPrimaryBg" style={{ width: "25%" }}>
                Actions
              </Table.HeaderCell>
              <Table.HeaderCell className="nectaPrimaryBg" style={{ width: "10%" }}>
                Rule Logs
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedRules.map((rule, index) => (
              <Table.Row
                key={`${rule.id}.${index}`}
                className="clickable"
                active={rule.id === state.selectedActivationRule.id}
              >
                <Table.Cell>{rule.name}</Table.Cell>
                <Table.Cell>{rule.description}</Table.Cell>
                <Table.Cell>
                  <StatusLabel status={rule.status} />
                  <TargetAudienceLabel targetAudience={rule.targetAudience} />
                  <MaxUsersLabel maxNumUsers={rule.maxNumUsers} />
                </Table.Cell>
                <Table.Cell>
                  {canExecuteActivationRule(props.userPermissions) && (
                    <Button
                      compact
                      className="primary"
                      size="small"
                      icon="play"
                      content="Run Now (Once)"
                      onClick={() => handleExecuteRule(rule)}
                    />
                  )}
                  {canUpdateActivationRule(props.userPermissions) && (
                    <FuseContentModal
                      header={`Edit Distribution Rule: ${rule.name}`}
                      content={<ActivationRuleDetails activationRule={rule} bdaction={"edit"} onComplete={() => updateState(setState, {
                        isEditActivationRuleOpen: false,
                        selectedActivationRule: {}
                      })} />}
                      trigger={<Button compact className="warning" icon="edit" size="small" content="Edit Rule" />}
                      onOpen={() => updateState(setState, {
                        isEditActivationRuleOpen: true,
                        selectedActivationRule: rule
                      })}
                      onClose={() => updateState(setState, {
                        isEditActivationRuleOpen: false,
                        selectedActivationRule: {}
                      })}
                    />
                  )}
                  {canDeleteActivationRule(props.userPermissions) && (
                    <Button
                      compact
                      className="danger"
                      icon="trash"
                      size="small"
                      onClick={(e) => handleDeleteActivationRule(e, rule)}
                    />
                  )}
                </Table.Cell>
                <Table.Cell>
                  <FuseContentModal
                    header={`Logs For Distribution Rule: ${rule.name}`}
                    content={<ActivationRuleLogDetails activationRule={rule} bdaction={"view"} />}
                    trigger={<Button compact className="primary" icon="eye" size="small" content="View Logs" />}
                    onOpen={() => updateState(setState, {
                      isEditActivationRuleOpen: true,
                      selectedActivationRule: rule
                    })}
                    onClose={() => updateState(setState, {
                      isEditActivationRuleOpen: false,
                      selectedActivationRule: {}
                    })}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>
    );
  };

  const renderContent = () => {
    if (props.fetchingData) return renderLoadingState();
    if (!props.activationRules || props.activationRules.length === 0) return renderEmptyState();
    return renderRulesTable();
  };

  return (
    <Container fluid>
      <Confirm
        content={`Are you sure you want to delete the following distribution rule: ${state.deleteActRuleCandidate.name}?`}
        open={state.showConfirmDeleteActRule}
        onCancel={() => updateState(setState, { showConfirmDeleteActRule: false })}
        onConfirm={confirmDeleteRule}
      />

      <Confirm
        content="Do you wish to push this questionnaire to your selected audience now? *Note this is a once off execution"
        open={state.showExecuteActivationRule}
        onCancel={() => updateState(setState, { showExecuteActivationRule: false })}
        onConfirm={confirmExecuteRule}
      />

      <Grid>
        <Grid.Row>
          <Grid.Column width="8">
            <h3>Distribution Rules:</h3>
          </Grid.Column>
          <Grid.Column width="8">
            {canCreateActivationRule(props.userPermissions) && (
              <FuseContentModal
                header="Create Distribution Rule"
                content={<ActivationRuleDetails bdaction={"add"} onComplete={() => updateState(setState, {
                  selectedActivationRule: {}
                })} />}
                trigger={
                  <Button
                    compact
                    className="success"
                    icon="add"
                    size="mini"
                    floated="right"
                    content="Create Rule"
                  />
                }
                onClose={() => updateState(setState, {
                  selectedActivationRule: {}
                })}
              />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {renderContent()}
    </Container>
  );
}

const mapStateToProps = (state) => ({
  activationRules: state.activationRules.rules,
  fetchingData: state.fetchingData === true,
  tenant: state.tenantManagement.activeTenant,
  userPermissions: _.isEmpty(state.authUserRoles.permissions)
    ? {}
    : state.authUserRoles.permissions,
  isPermissionsFetched: state.isPermissionsFetched === true,
});

export default connect(
  mapStateToProps,
  (dispatch) => ({
    fetchActivationRulesAction: (tenant) =>
      dispatch(ACTIVATION_RULES.fetchActivationRulesAction(tenant)),
    deleteActivationRuleAction: (rule, tenant) =>
      dispatch(ACTIVATION_RULES.deleteActivationRuleAction(rule, tenant)),
    executeActivartionRuleAction: (rule) =>
      dispatch(ACTIVATION_RULES.executeActivartionRuleAction(rule)),
  })
)(ActivationRulesPage);
